import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { useOptionalTenantContext } from '../contexts/TenantContext';

export type EstimationType = 'STORY_POINTS' | 'EU_RU' | 'BOTH';

export interface MyTenant {
  tenantId: string;
  role: string;
  tenantName: string;
  onboardingStatus: string;
  estimationType: EstimationType;
  subscriptionTier: string;
  /**
   * True when the authenticated user is a member of a partner_accounts row.
   * Identity is orthogonal to subscriptionTier — partners hold the
   * PLATFORM_PARTNER tier in production. Gate billing portal access on
   * this flag, not on the tier string.
   */
  isPartner?: boolean;
  /**
   * #3185 APPROVE.2 2c (R1 P3-2) — may the caller confirm a partner-invited
   * approver in this workspace? The confirm route's own check
   * (approverConfirmerStanding): the client's own admin or the MANAGED
   * contact, never partner-marked, partner staff of a linked partner, or
   * awaiting their own confirmation. `undefined` on legacy responses, treated
   * as no; the server re-checks either way.
   */
  canConfirmApprovers?: boolean;
  /**
   * IANA timezone the tenant configured during onboarding, stored nested
   * inside `agentConfigOverride.timezone` on the server. Used by
   * CFG.I18N.A2 to pick a display currency — `Asia/Kolkata` → INR, any
   * other string → USD. `null` when the tenant hasn't set one yet
   * (pre-onboarding or legacy rows).
   */
  timezone?: string | null;
  /**
   * Sprint 12.5 ACC.B6 — opt-in feature flag for the per-section
   * thumbs-up/down feedback UI on artefact pages. Default `false` —
   * only true when the tenant admin enables it via Settings →
   * Organisation. ArtefactDetailContent hides the entire feedback row
   * when this is false.
   */
  qualityFeedbackEnabled?: boolean;
  /**
   * Sprint UXV UXV.F4 (#1420) — tenant-level default for the Claude Design
   * export toggle. Read by the per-project override card (as the inherit
   * value) and by F2's export-action gate. `false` when unset/legacy.
   */
  claudeDesignExportEnabled?: boolean;
  /**
   * MODE.A2 (#1171) — Free Trial billing state. Wizards
   * (NewProjectModal, BrownfieldOnboardingPage) hide the
   * DeliveryModePicker when this is `'TRIAL'`: Trial tenants get the
   * Accelerate default at the API layer and don't need the picker UI.
   * Values: `'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED'`. Backend
   * defaults to `'TRIAL'` when the tenant row is missing (fail-OPEN to
   * "Trial tenant" behaviour — picker hidden — for consistency with
   * the DB column default).
   */
  billingState?: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
  /**
   * epic #1247 (D4b) — ring-flow setup completion signal, surfaced on
   * /tenants/me/membership so the Sidebar can show a persistent "Setup"
   * nav item WHILE setup is incomplete (and hide it once done) without a
   * second round-trip. `false` only while an active (in_progress|paused)
   * setup project still has outstanding tasks; `undefined` on legacy
   * responses (treated as complete → item hidden).
   */
  setupComplete?: boolean;
  /**
   * #1247 access-regression follow-on — per-caller /setup eligibility,
   * computed server-side by the SAME predicate the /setup gate enforces
   * (the assigned Technical Lead, or the Buyer while no TL is assigned).
   * The Sidebar shows the "Setup" item only when `setupComplete === false`
   * AND `setupAccessible === true`, so it never routes a caller the gate
   * would 403 (the Buyer once a TL is assigned, or any non-Buyer/non-TL
   * member). `undefined` on legacy responses → treated as not-accessible
   * (item hidden), matching the fail-closed `=== true` check.
   */
  setupAccessible?: boolean;
  /**
   * CFG-IA.A1 (#1536) — tenant deployment mode, surfaced on
   * /tenants/me/membership so Settings → Infrastructure can seed its editable
   * BYOC/MANAGED toggle and decide whether to render the cloud-credential
   * surface without a second round-trip. Server mirrors the DB column default
   * ('BYOC') when unset; the page narrows any non-'MANAGED' value to BYOC.
   */
  infrastructureMode?: 'BYOC' | 'MANAGED';
}

/** Role may be stored as TENANT_ADMIN or ADMIN depending on when the account was created */
export function isAdminRole(role: string | undefined): boolean {
  return role === 'TENANT_ADMIN' || role === 'ADMIN';
}

/**
 * WS.C1 — shim over TenantContext's shared /tenants/me/membership fetch when
 * a TenantProvider ancestor is present (the real app, wrapped in App.tsx).
 * Falls back to the pre-refactor standalone fetch when it isn't — many
 * existing component tests render a useMyTenant() consumer in isolation with
 * no AuthProvider/TenantProvider wrapper, and this keeps them working
 * unchanged. Return shape ({ myTenant, loading }) is identical either way.
 */
export function useMyTenant() {
  const ctx = useOptionalTenantContext();
  const [standalone, setStandalone] = useState<{ myTenant: MyTenant | null; loading: boolean }>({
    myTenant: null,
    loading: true,
  });

  useEffect(() => {
    if (ctx) return;
    let cancelled = false;
    apiFetch<MyTenant>('/tenants/me/membership')
      .then((mt) => { if (!cancelled) setStandalone({ myTenant: mt, loading: false }); })
      .catch(() => { if (!cancelled) setStandalone({ myTenant: null, loading: false }); });
    return () => { cancelled = true; };
  }, [ctx]);

  if (ctx) return { myTenant: ctx.myTenant, loading: ctx.myTenantLoading };
  return standalone;
}
